import React from 'react'
import { Button, Input, Icon, IconButton } from '@shopflo/ui'
import { CurrencyInr, X } from '@phosphor-icons/react'
import { useDiscountForm } from '../../../hooks/useDiscountForm'
import { PurchaseConditionType, CouponType, PrerequisiteType } from 'src/lib/types/discountsV2'

import FormErrorText from 'src/components/common/FormErrorText'

const PurchaseConditionsCartInputs = () => {
  const {
    state: { prerequisite: rawPrerequisite, errors },
    actions: { setFormState }
  } = useDiscountForm()

  if (rawPrerequisite.type === CouponType.BUNDLE) return null
  const prerequisite = rawPrerequisite

  const activeConfig = prerequisite

  const isAmount = activeConfig?.purchaseConditionType === PurchaseConditionType.CART_AMOUNT
  const isQuantity = activeConfig?.purchaseConditionType === PurchaseConditionType.CART_QUANTITY

  if (!isAmount && !isQuantity) return null

  const prerequisiteSubtotalRange =
    prerequisite.prerequisiteType === PrerequisiteType.PRICE ? prerequisite.prerequisiteSubtotalRange : null
  const prerequisiteMaxOrderValue =
    prerequisite.prerequisiteType === PrerequisiteType.PRICE ? prerequisite.prerequisiteMaxOrderValue : null

  const prerequisiteCountToEntitlement =
    prerequisite.prerequisiteType === PrerequisiteType.QUANTITY ? prerequisite.prerequisiteCountToEntitlement : null
  const prerequisiteMaxCountForEntitlement =
    prerequisite.prerequisiteType === PrerequisiteType.QUANTITY ? prerequisite.prerequisiteMaxCountForEntitlement : null

  const commonClearPaths = isAmount
    ? ['prerequisite.prerequisiteMaxOrderValue', 'prerequisite.prerequisiteSubtotalRange']
    : ['prerequisite.prerequisiteMaxCountForEntitlement', 'prerequisite.prerequisiteCountToEntitlement']

  const title = isAmount ? 'Specific cart amount' : 'Specific cart quantity'

  const renderMaxLimitInput = (type: PrerequisiteType.PRICE | PrerequisiteType.QUANTITY) => {
    const isPrice = type === PrerequisiteType.PRICE
    const maxValue = isPrice ? prerequisiteMaxOrderValue : prerequisiteMaxCountForEntitlement

    if (maxValue !== null) {
      return (
        <>
          <span className='text-text-xs-medium text-zinc-500'>Maximum (Optional)</span>
          <div className='relative flex items-center'>
            <Input
              id='purchase-condition-max-input'
              value={maxValue ?? ''}
              onChange={e => {
                const value = parseFloat(e.target.value)

                setFormState(
                  {
                    prerequisite: {
                      ...prerequisite,
                      [isPrice ? 'prerequisiteMaxOrderValue' : 'prerequisiteMaxCountForEntitlement']: value
                    }
                  },
                  commonClearPaths
                )
              }}
              placeholder='0'
              startAdornment={
                isPrice ? <Icon IconComponent={CurrencyInr} size='sm' className='text-zinc-400' /> : undefined
              }
              className='w-full'
              dimension='md'
              type='number'
              endAdornment={
                <IconButton
                  id='purchase-condition-clear-max-button'
                  variant='ghost'
                  size='xs'
                  IconComponent={X}
                  onClick={() => {
                    setFormState(
                      {
                        prerequisite: {
                          ...prerequisite,
                          [isPrice ? 'prerequisiteMaxOrderValue' : 'prerequisiteMaxCountForEntitlement']: null
                        }
                      },
                      commonClearPaths
                    )
                  }}
                  className='!text-zinc-400 hover:!text-zinc-600'
                />
              }
            />
          </div>
        </>
      )
    }

    return (
      <Button
        id='purchase-condition-set-limit-button'
        variant='dashed'
        onClick={() => {
          setFormState(
            {
              prerequisite: {
                ...prerequisite,
                [isPrice ? 'prerequisiteMaxOrderValue' : 'prerequisiteMaxCountForEntitlement']: 0
              }
            },
            commonClearPaths
          )
        }}
        size='md'
        className='bg-transparent'
      >
        Set limit
      </Button>
    )
  }

  // Amount Inputs
  if (isAmount) {
    return (
      <div className='flex flex-col gap-3 mb-1 w-[50%]'>
        <div className='flex gap-3 w-full'>
          <div className='flex-1 flex flex-col gap-1'>
            <span className='text-text-xs-medium text-zinc-500'>Minimum</span>
            <Input
              id='purchase-condition-min-input'
              value={prerequisiteSubtotalRange ?? ''}
              onChange={e => {
                if (prerequisite.prerequisiteType === PrerequisiteType.PRICE) {
                  setFormState(
                    {
                      prerequisite: {
                        ...prerequisite,
                        prerequisiteSubtotalRange: parseFloat(e.target.value)
                      }
                    },
                    commonClearPaths
                  )
                }
              }}
              placeholder='0'
              startAdornment={<Icon IconComponent={CurrencyInr} size='sm' className='text-zinc-400' />}
              className='w-full'
              dimension='md'
              type='number'
              error={!!errors['prerequisite.prerequisiteSubtotalRange']}
            />
          </div>
          <div className='flex-1 flex flex-col gap-1 justify-end'>{renderMaxLimitInput(PrerequisiteType.PRICE)}</div>
        </div>
        <FormErrorText
          message={errors['prerequisite.prerequisiteSubtotalRange'] || errors['prerequisite.prerequisiteMaxOrderValue']}
        />
      </div>
    )
  }

  // Quantity Inputs
  return (
    <div className='flex flex-col gap-3 mb-1 w-[50%]'>
      <div className='flex gap-3 w-full'>
        <div className='flex-1 flex flex-col gap-1'>
          <span className='text-text-xs-medium text-zinc-500'>Minimum</span>
          <Input
            id='purchase-condition-min-input-qty'
            value={prerequisiteCountToEntitlement || ''}
            onChange={e => {
              if (prerequisite.prerequisiteType === PrerequisiteType.QUANTITY) {
                setFormState(
                  {
                    prerequisite: {
                      ...prerequisite,
                      prerequisiteCountToEntitlement: parseInt(e.target.value)
                    }
                  },
                  commonClearPaths
                )
              }
            }}
            placeholder='0'
            className='w-full'
            dimension='md'
            type='number'
            error={
              !!errors['prerequisite.prerequisiteCountToEntitlement'] ||
              !!errors['prerequisite.prerequisiteMaxCountForEntitlement']
            }
          />
        </div>

        <div className='flex-1 flex flex-col gap-1 justify-end'>{renderMaxLimitInput(PrerequisiteType.QUANTITY)}</div>
      </div>

      <FormErrorText
        message={
          errors['prerequisite.prerequisiteCountToEntitlement'] ||
          errors['prerequisite.prerequisiteMaxCountForEntitlement']
        }
      />
    </div>
  )
}

export default PurchaseConditionsCartInputs
